import { useState, useEffect, useCallback, useMemo } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import useAppStore from '../../store/app.store';

const calcularCorUrgencia = (dataAbertura, dataPrazo) => {
  const total    = new Date(dataPrazo) - new Date(dataAbertura);
  const restante = new Date(dataPrazo) - new Date();
  if (restante <= 0) return 'vermelho';
  const pct = restante / total;
  if (pct > 0.5)  return 'verde';
  if (pct > 0.25) return 'amarelo';
  return 'laranja';
};

const COR_BORDA = { verde: '#4ADE80', amarelo: '#FBBF24', laranja: '#F97316', vermelho: '#F87171' };

const PRIO_COR = { urgente: '#EF4444', alta: '#F97316', media: '#6C4CF5', baixa: '#22C55E' };

const STAT_CORES = {
  amarelo:  { icon: 'rgba(251,191,36,.15)',  text: '#FBBF24' },
  verde:    { icon: 'rgba(74,222,128,.15)',   text: '#4ADE80' },
  azul:     { icon: 'rgba(56,189,248,.15)',  text: '#38BDF8' },
  roxo:     { icon: 'rgba(108,76,245,.15)',  text: '#8D72FF' },
  vermelho: { icon: 'rgba(248,113,113,.15)', text: '#F87171' },
};

const ETAPAS = [
  { status: 'copy',                 label: 'Copy',          accent: '#94A3B8' },
  { status: 'roteiro',              label: 'Roteiro',        accent: '#818CF8' },
  { status: 'criativo',             label: 'Criativo',       accent: '#A78BFA' },
  { status: 'captacao',             label: 'Captação',       accent: '#38BDF8' },
  { status: 'apresentacao',         label: 'Apresentação',   accent: '#22D3EE' },
  { status: 'aguardando_aprovacao', label: 'Ag. Aprovação',  accent: '#F472B6' },
  { status: 'em_ajuste',            label: 'Em ajuste',      accent: '#F87171' },
  { status: 'agendado',             label: 'Agendados',      accent: '#4ADE80' },
];

const FILTRO_LABEL = {
  pendentes:          'Chamados pendentes',
  concluidas_hoje:    'Concluídos hoje',
  concluidas_semana:  'Concluídos esta semana',
  concluidas_mes:     'Concluídos este mês',
  atrasados:          'Em atraso',
};

const ICON = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);

function StatCard({ label, value, icon, cor = 'azul', ativo, onClick }) {
  const c = STAT_CORES[cor];
  return (
    <button onClick={onClick} style={{
      background: ativo ? 'rgba(108,76,245,.1)' : '#19243A',
      border: `1px solid ${ativo ? c.text + '60' : '#2A3652'}`,
      borderRadius: 14, padding: '16px 18px', textAlign: 'left',
      width: '100%', cursor: 'pointer', transition: 'all .2s',
    }}
    onMouseEnter={e => { if (!ativo) e.currentTarget.style.background = '#1F2B45'; }}
    onMouseLeave={e => { if (!ativo) e.currentTarget.style.background = '#19243A'; }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{label}</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: '#E2E8F4', lineHeight: 1 }}>{value}</p>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: c.icon, color: c.text, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </div>
      </div>
      {ativo && <p style={{ fontSize: 11, marginTop: 10, color: '#8D72FF', fontWeight: 600 }}>▼ filtrando</p>}
    </button>
  );
}

export default function Dashboard() {
  const usuario  = useAppStore(s => s.usuario);
  const isAdmin  = parseInt(usuario?.nivel_acesso) <= 1;

  const [eu, setEu]               = useState(null);
  const [stats, setStats]         = useState({ pendentes: 0, concluidas_hoje: 0, concluidas_semana: 0, concluidas_mes: 0, atrasados: 0 });
  const [chamados, setChamados]   = useState([]);
  const [producoes, setProducoes] = useState([]);
  const [clientes, setClientes]   = useState([]);
  const [notificacoes, setNotifs] = useState([]);
  const [topColabs, setTopColabs] = useState([]);
  const [porSetor, setPorSetor]   = useState([]);
  const [filtroAtivo, setFiltro]  = useState(null);
  const [loading, setLoading]     = useState(true);
  const [meuDia, setMeuDia]       = useState({ atrasados: [], hoje: [], semana: [] });
  const [workload, setWorkload]   = useState([]);

  const carregarMeuDia = useCallback(async () => {
    try {
      const { data } = await api.get('/chamados', { params: { limit: 200 } });
      // A API devolve { data: [...], total } — não um array direto
      const lista = Array.isArray(data) ? data : (data?.data || []);
      const agora = new Date();
      const hoje = new Date(); hoje.setHours(23,59,59,999);
      const semana = new Date(Date.now() + 7 * 86400000);
      const ativos = lista.filter(c => !['concluido','cancelado'].includes(c.status));
      setMeuDia({
        atrasados: ativos.filter(c => c.data_prazo && new Date(c.data_prazo) < agora),
        hoje: ativos.filter(c => c.data_prazo && new Date(c.data_prazo) >= agora && new Date(c.data_prazo) <= hoje),
        semana: ativos.filter(c => c.data_prazo && new Date(c.data_prazo) > hoje && new Date(c.data_prazo) <= semana),
      });
    } catch {}
  }, []);

  const carregar = useCallback(async () => {
    try {
      const [dash, chs, prods, cls, notifs, meData] = await Promise.all([
        api.get('/dashboard'),
        api.get('/chamados'),
        api.get('/midias/producoes'),
        api.get('/clientes/lista'),
        api.get('/notificacoes'),
        api.get('/auth/me'),
      ]);
      setStats(dash.data.cards || {});
      setTopColabs(dash.data.top_colaboradores || []);
      setPorSetor(dash.data.por_setor || []);
      setChamados(Array.isArray(chs.data) ? chs.data : (chs.data?.data || []));
      setProducoes(prods.data || []);
      setClientes(cls.data || []);
      setNotifs(notifs.data || []);
      setEu(meData.data);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    carregar();
    carregarMeuDia();
    if (isAdmin) {
      api.get('/dashboard/workload').then(r => setWorkload(r.data || [])).catch(() => {});
    }
  }, [carregar, carregarMeuDia, isAdmin]);

  const clienteNome = (id) => {
    const c = clientes.find(x => x.id === id);
    return c?.nome_fantasia || c?.razao_social || '—';
  };

  const naoLidas = notificacoes.filter(n => !n.lida).length;

  const marcarLida = async (id) => {
    try {
      await api.patch(`/notificacoes/${id}`);
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    } catch {}
  };

  const marcarTodasLidas = async () => {
    try {
      await api.patch('/notificacoes/lidas');
      setNotifs(prev => prev.map(n => ({ ...n, lida: true })));
    } catch {}
  };

  // Chamados filtrados conforme card clicado
  const chamadosExibidos = useMemo(() => {
    const hoje      = new Date().toDateString();
    const semana    = new Date(Date.now() - 7 * 86400000);
    const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const ativos    = c => !['concluido', 'cancelado'].includes(c.status);

    const filtros = {
      pendentes:         c => ativos(c),
      concluidas_hoje:   c => c.status === 'concluido' && c.data_conclusao && new Date(c.data_conclusao).toDateString() === hoje,
      concluidas_semana: c => c.status === 'concluido' && c.data_conclusao && new Date(c.data_conclusao) >= semana,
      concluidas_mes:    c => c.status === 'concluido' && c.data_conclusao && new Date(c.data_conclusao) >= inicioMes,
      atrasados:         c => ativos(c) && new Date(c.data_prazo) < new Date(),
    };

    const lista = filtroAtivo
      ? chamados.filter(filtros[filtroAtivo])
      : chamados.filter(c => ativos(c)).slice(0, 8);

    return lista.sort((a, b) => new Date(a.data_prazo || 0) - new Date(b.data_prazo || 0));
  }, [chamados, filtroAtivo]);

  // Produções do usuário com prazo hoje
  const minhasProducoesHoje = useMemo(() => {
    if (!eu) return [];
    return producoes.filter(p => {
      if (!p.prazo || p.status === 'publicado') return false;
      if (p.responsavel_id !== eu.id && p.criado_por !== eu.id) return false;
      return new Date(p.prazo).toDateString() === new Date().toDateString();
    });
  }, [producoes, eu]);

  const toggleFiltro = (k) => setFiltro(prev => prev === k ? null : k);

  const STATUS_LABEL = { pendente: 'Pendente', em_andamento: 'Em andamento', em_revisao: 'Em revisão', aguardando_cliente: 'Ag. Cliente', concluido: 'Concluído', cancelado: 'Cancelado' };
  const STATUS_COR = { pendente: '#FBBF24', em_andamento: '#38BDF8', em_revisao: '#A78BFA', aguardando_cliente: '#F97316', concluido: '#4ADE80', cancelado: '#64748B' };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
      <p style={{ color: '#64748B' }}>Carregando dashboard...</p>
    </div>
  );

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Visão geral em tempo real" />

      {/* MEU DIA */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.08em' }}>Meu Dia</p>
          <div style={{ flex: 1, height: 1, background: '#243356' }} />
          <p style={{ fontSize: 11, color: '#64748B' }}>{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {/* ATRASADOS */}
          <div style={{ background: '#19243A', border: '1px solid rgba(239,68,68,.3)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(239,68,68,.08)', padding: '12px 16px', borderBottom: '1px solid rgba(239,68,68,.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#EF4444' }}>⚠ Atrasados</p>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'rgba(239,68,68,.15)', padding: '2px 8px', borderRadius: 999 }}>{meuDia.atrasados.length}</span>
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
              {meuDia.atrasados.length === 0 && <p style={{ fontSize: 12, color: '#64748B', textAlign: 'center', padding: '12px 0' }}>Nenhum atraso</p>}
              {meuDia.atrasados.map(t => (
                <div key={t.id} style={{ background: '#131D31', borderRadius: 8, padding: '8px 12px', borderLeft: `3px solid ${PRIO_COR[t.prioridade] || '#EF4444'}` }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0', marginBottom: 3 }}>{t.titulo}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 10, color: '#64748B' }}>{t.cliente_nome || 'Sem cliente'}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: PRIO_COR[t.prioridade] || '#EF4444', textTransform: 'uppercase' }}>{t.prioridade}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* VENCE HOJE */}
          <div style={{ background: '#19243A', border: '1px solid rgba(34,197,94,.3)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(34,197,94,.08)', padding: '12px 16px', borderBottom: '1px solid rgba(34,197,94,.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#22C55E' }}>📅 Vence Hoje</p>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', background: 'rgba(34,197,94,.15)', padding: '2px 8px', borderRadius: 999 }}>{meuDia.hoje.length}</span>
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
              {meuDia.hoje.length === 0 && <p style={{ fontSize: 12, color: '#64748B', textAlign: 'center', padding: '12px 0' }}>Nada para hoje</p>}
              {meuDia.hoje.map(t => (
                <div key={t.id} style={{ background: '#131D31', borderRadius: 8, padding: '8px 12px', borderLeft: `3px solid ${PRIO_COR[t.prioridade] || '#22C55E'}` }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0', marginBottom: 3 }}>{t.titulo}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 10, color: '#64748B' }}>{t.cliente_nome || 'Sem cliente'}</span>
                    <span style={{ fontSize: 10, color: '#22C55E' }}>{t.data_prazo ? new Date(t.data_prazo).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ESTA SEMANA */}
          <div style={{ background: '#19243A', border: '1px solid rgba(56,189,248,.3)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(56,189,248,.08)', padding: '12px 16px', borderBottom: '1px solid rgba(56,189,248,.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#38BDF8' }}>📆 Esta Semana</p>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#38BDF8', background: 'rgba(56,189,248,.15)', padding: '2px 8px', borderRadius: 999 }}>{meuDia.semana.length}</span>
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
              {meuDia.semana.length === 0 && <p style={{ fontSize: 12, color: '#64748B', textAlign: 'center', padding: '12px 0' }}>Semana limpa</p>}
              {meuDia.semana.map(t => (
                <div key={t.id} style={{ background: '#131D31', borderRadius: 8, padding: '8px 12px', borderLeft: `3px solid ${PRIO_COR[t.prioridade] || '#38BDF8'}` }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0', marginBottom: 3 }}>{t.titulo}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 10, color: '#64748B' }}>{t.cliente_nome || 'Sem cliente'}</span>
                    <span style={{ fontSize: 10, color: '#38BDF8' }}>{t.data_prazo ? new Date(t.data_prazo).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="resp-grid-5" style={{ marginBottom: 24 }}>
        <StatCard label="Pendentes"       value={stats.pendentes}         cor="amarelo"  ativo={filtroAtivo === 'pendentes'}         onClick={() => toggleFiltro('pendentes')}
          icon={<ICON d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z','M12 6v6l4 2']} />} />
        <StatCard label="Concluídos hoje" value={stats.concluidas_hoje}   cor="verde"    ativo={filtroAtivo === 'concluidas_hoje'}   onClick={() => toggleFiltro('concluidas_hoje')}
          icon={<ICON d="M20 6 9 17l-5-5" />} />
        <StatCard label="Esta semana"     value={stats.concluidas_semana} cor="azul"     ativo={filtroAtivo === 'concluidas_semana'} onClick={() => toggleFiltro('concluidas_semana')}
          icon={<ICON d={['M8 2v4M16 2v4M3 10h18','M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z']} />} />
        <StatCard label="Este mês"        value={stats.concluidas_mes}    cor="roxo"     ativo={filtroAtivo === 'concluidas_mes'}    onClick={() => toggleFiltro('concluidas_mes')}
          icon={<ICON d={['M8 2v4M16 2v4M3 10h18M8 14h8','M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z']} />} />
        <StatCard label="Em atraso"       value={stats.atrasados}         cor="vermelho" ativo={filtroAtivo === 'atrasados'}         onClick={() => toggleFiltro('atrasados')}
          icon={<ICON d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z','M12 8v4M12 16h.01']} />} />
      </div>

      {/* Alerta: produções com prazo hoje */}
      {minhasProducoesHoje.length > 0 && (
        <div style={{ background: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.25)', borderRadius: 14, padding: '14px 18px', marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: '#FBBF24', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            📌 Minhas produções com prazo hoje
            <span style={{ background: '#FBBF24', color: '#111827', fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '1px 7px' }}>{minhasProducoesHoje.length}</span>
          </h3>
          <div className="resp-grid-3" style={{ gap: 8 }}>
            {minhasProducoesHoje.map(p => (
              <div key={p.id} style={{ background: '#19243A', border: '1px solid rgba(251,191,36,.2)', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#E2E8F4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.titulo}</p>
                  <p style={{ fontSize: 11, color: '#64748B' }}>{clienteNome(p.cliente_id)} · {p.plataforma}</p>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#FBBF24', background: 'rgba(251,191,36,.12)', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>
                  {p.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chamados + Notificações */}
      <div className="resp-2-1" style={{ gap: 16, marginBottom: 24 }}>
        {/* Lista de chamados */}
        <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: '#94A3B8' }}>
              {filtroAtivo ? FILTRO_LABEL[filtroAtivo] : 'Chamados com prazo mais próximo'}
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#475569' }}>({chamadosExibidos.length})</span>
            </h3>
            {filtroAtivo && (
              <button onClick={() => setFiltro(null)} style={{ fontSize: 12, color: '#6C4CF5', background: 'none', border: 'none', cursor: 'pointer' }}>Limpar ×</button>
            )}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {chamadosExibidos.map(ch => {
              const cor = calcularCorUrgencia(ch.data_abertura, ch.data_prazo);
              const stCor = STATUS_COR[ch.status] || '#94A3B8';
              return (
                <div key={ch.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderRadius: 10, background: 'rgba(255,255,255,.03)',
                  borderLeft: `3px solid ${COR_BORDA[cor]}`,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#E2E8F4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.titulo}</p>
                    <p style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{clienteNome(ch.cliente_id)} · {ch.setor}</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: stCor, background: `${stCor}18`, padding: '2px 7px', borderRadius: 5 }}>
                      {STATUS_LABEL[ch.status] || ch.status}
                    </span>
                    {ch.data_prazo && <p style={{ fontSize: 10, color: '#475569', marginTop: 3 }}>{new Date(ch.data_prazo).toLocaleDateString('pt-BR')}</p>}
                  </div>
                </div>
              );
            })}
            {chamadosExibidos.length === 0 && (
              <p style={{ fontSize: 13, color: '#475569', textAlign: 'center', padding: '24px 0' }}>Nenhum chamado encontrado.</p>
            )}
          </div>
        </div>

        {/* Notificações */}
        <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ICON d={['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9','M13.73 21a2 2 0 0 1-3.46 0']} size={14} />
              Notificações
              {naoLidas > 0 && <span style={{ background: '#F87171', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '1px 6px' }}>{naoLidas}</span>}
            </h3>
            {naoLidas > 0 && (
              <button onClick={marcarTodasLidas} style={{ fontSize: 12, color: '#6C4CF5', background: 'none', border: 'none', cursor: 'pointer' }}>Marcar lidas</button>
            )}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {notificacoes.length === 0 && (
              <p style={{ fontSize: 13, color: '#475569', textAlign: 'center', padding: '24px 0' }}>Nenhuma notificação.</p>
            )}
            {notificacoes.map(n => (
              <div key={n.id} onClick={() => !n.lida && marcarLida(n.id)} style={{
                padding: '10px 12px', borderRadius: 10, cursor: n.lida ? 'default' : 'pointer',
                background: n.lida ? 'rgba(255,255,255,.02)' : 'rgba(108,76,245,.08)',
                border: `1px solid ${n.lida ? '#2A3652' : 'rgba(108,76,245,.25)'}`,
                transition: 'all .15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ color: '#6C4CF5', flexShrink: 0, marginTop: 1 }}>
                    <ICON d={['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9','M13.73 21a2 2 0 0 1-3.46 0']} size={13} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: n.lida ? '#64748B' : '#E2E8F4' }}>{n.titulo}</p>
                    {n.mensagem && <p style={{ fontSize: 11, color: '#64748B', marginTop: 2, lineHeight: 1.4 }}>{n.mensagem}</p>}
                    <p style={{ fontSize: 10, color: '#475569', marginTop: 4 }}>
                      {format(new Date(n.criado_em), "dd/MM 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                  {!n.lida && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#6C4CF5', flexShrink: 0, marginTop: 4 }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Produções por etapa */}
      <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: '18px 20px', marginBottom: 24 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: '#94A3B8', marginBottom: 14 }}>Produções por etapa</h3>
        <div className="resp-grid-8" style={{ gap: 10 }}>
          {ETAPAS.map(item => {
            const qtd = producoes.filter(p => p.status === item.status).length;
            return (
              <div key={item.status} style={{
                background: qtd > 0 ? `${item.accent}18` : 'rgba(255,255,255,.03)',
                border: `1px solid ${qtd > 0 ? item.accent + '40' : '#2A3652'}`,
                borderRadius: 10, padding: '12px 8px', textAlign: 'center',
                opacity: qtd === 0 ? 0.5 : 1, transition: 'all .2s',
              }}>
                <p style={{ fontSize: 26, fontWeight: 700, color: qtd > 0 ? item.accent : '#475569', lineHeight: 1 }}>{qtd}</p>
                <p style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', marginTop: 6, lineHeight: 1.3 }}>{item.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Carga da Equipe (admin only) */}
      {isAdmin && workload.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.08em' }}>Carga da Equipe</p>
            <div style={{ flex: 1, height: 1, background: '#243356' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {workload.map(u => {
              const total = parseInt(u.total_abertos) || 0;
              const maxLoad = 10;
              const pct = Math.min((total / maxLoad) * 100, 100);
              const cor = total >= 8 ? '#EF4444' : total >= 5 ? '#F97316' : '#22C55E';
              return (
                <div key={u.id} style={{ background: '#19243A', border: '1px solid #243356', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0' }}>{u.nome?.split(' ')[0]}</p>
                    <span style={{ fontSize: 11, fontWeight: 700, color: cor }}>{total} abertas</span>
                  </div>
                  <div style={{ height: 4, background: '#243356', borderRadius: 999, marginBottom: 10 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: cor, borderRadius: 999 }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    {[
                      { l: 'Atrasadas', v: u.atrasados, c: '#EF4444' },
                      { l: 'Hoje', v: u.vence_hoje, c: '#22C55E' },
                      { l: 'Semana', v: u.vence_semana, c: '#38BDF8' },
                      { l: 'Concluídas hoje', v: u.concluidos_hoje, c: '#94A3B8' },
                    ].map(m => (
                      <div key={m.l} style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: 16, fontWeight: 700, color: m.c }}>{m.v || 0}</p>
                        <p style={{ fontSize: 9, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.04em' }}>{m.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top colaboradores + Por setor */}
      {(topColabs.length > 0 || porSetor.length > 0) && (
        <div className="resp-1-1" style={{ gap: 16 }}>
          {topColabs.length > 0 && (
            <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: '18px 20px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: '#94A3B8', marginBottom: 14 }}>Top colaboradores (mês)</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {topColabs.map((c, i) => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? '#FBBF24' : '#64748B', width: 18 }}>#{i + 1}</span>
                    <div style={{ width: 28, height: 28, borderRadius: '28%', background: '#6C4CF5', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {c.nome?.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#E2E8F4' }}>{c.nome}</p>
                      <p style={{ fontSize: 10, color: '#64748B' }}>Tempo médio: {Number(c.tempo_medio_horas || 0).toFixed(1)}h</p>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#4ADE80' }}>{c.total_mes}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {porSetor.length > 0 && (
            <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: '18px 20px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: '#94A3B8', marginBottom: 14 }}>Chamados por setor</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {porSetor.map(s => {
                  const max = porSetor[0]?.total || 1;
                  const pct = Math.round((s.total / max) * 100);
                  return (
                    <div key={s.setor}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#CBD5E1', fontWeight: 500, textTransform: 'capitalize' }}>{s.setor}</span>
                        <span style={{ color: '#64748B', fontWeight: 600 }}>{s.total}</span>
                      </div>
                      <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,.06)' }}>
                        <div style={{ height: '100%', borderRadius: 999, background: '#6C4CF5', width: `${pct}%`, transition: 'width .4s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
